import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../component/Navbar/Navbar";

/**
 * Desktop SSO handoff page.
 *
 * After a successful desktop login, the backend redirects here with the auth
 * tokens in the query string. This page shows a "signing you in" state with a
 * loading mic animation, then automatically deep-links back into the native app
 * via its custom URL scheme, handing the tokens over so the app becomes logged in.
 *
 * Scheme matches the existing SignupSuccess convention ("zerotypingai://…").
 * Override with REACT_APP_DESKTOP_SCHEME if the app team registered a different one.
 */
const APP_SCHEME =
  process.env.REACT_APP_DESKTOP_SCHEME || "zerotypingai://signin-success";

const MicIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-9 h-9"
    aria-hidden="true"
  >
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 10v1a7 7 0 0 0 14 0v-1" />
    <line x1="12" y1="18" x2="12" y2="22" />
    <line x1="8" y1="22" x2="16" y2="22" />
  </svg>
);

const SignInSuccessful = () => {
  const [opened, setOpened] = useState(false);
  // True once the native app actually takes focus (browser tab becomes
  // hidden/blurred). We stop the loading animation at that point.
  const [appOpened, setAppOpened] = useState(false);

  const deepLink = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const refresh = params.get("refresh");
    if (!token) return "";
    const out = new URLSearchParams({ token, refresh: refresh || "" });
    return `${APP_SCHEME}?${out.toString()}`;
  }, []);

  useEffect(() => {
    if (!deepLink) return;
    // Auto-open the native app after a short beat so the animation is visible.
    const t = setTimeout(() => {
      window.location.href = deepLink;
      setOpened(true);
    }, 1200);
    return () => clearTimeout(t);
  }, [deepLink]);

  // Detect when the native app has actually opened: launching it via the custom
  // URL scheme pulls focus away from the browser, so the tab gets hidden/blurred.
  // Once that happens we latch appOpened = true and stop the loading animation.
  useEffect(() => {
    const markOpened = () => {
      if (document.visibilityState === "hidden") setAppOpened(true);
    };
    const onBlur = () => setAppOpened(true);
    document.addEventListener("visibilitychange", markOpened);
    window.addEventListener("blur", onBlur);
    window.addEventListener("pagehide", onBlur);
    return () => {
      document.removeEventListener("visibilitychange", markOpened);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("pagehide", onBlur);
    };
  }, []);

  return (
    <div className="bg-white md:p-4 p-3 max-w-screen-2xl mx-auto min-h-screen flex flex-col">
      <Navbar />

      <div className="relative mt-6 md:mt-0 rounded-2xl overflow-hidden border border-[#3AB1F626] flex-1 flex flex-col">
        <div className="absolute inset-0 bg-[#D8EFFD] opacity-50 pointer-events-none" />

        <div
          className="absolute pointer-events-none rounded-full"
          style={{
            width: "min(650px, 75vw)",
            height: "min(650px, 75vw)",
            left: "-15%",
            bottom: "-25%",
            background: "#3AB1F6",
            opacity: 0.65,
            filter: "blur(140px)",
            WebkitFilter: "blur(140px)",
          }}
        />
        <div
          className="absolute pointer-events-none rounded-full"
          style={{
            width: "min(650px, 75vw)",
            height: "min(650px, 75vw)",
            right: "-15%",
            bottom: "-25%",
            background: "#3AB1F6",
            opacity: 0.65,
            filter: "blur(140px)",
            WebkitFilter: "blur(140px)",
          }}
        />

        <style>{`
          @keyframes zt-ring-spin { to { transform: rotate(360deg); } }
          @keyframes zt-mic-pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.08); opacity: 0.85; }
          }
          @keyframes zt-fade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>

        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 sm:px-6 md:px-8 py-12 sm:py-16 md:py-20">
          {/* Mic: spinning ring + pulsing icon while loading; static once the
              app has opened. */}
          <div className="relative w-[110px] h-[110px] sm:w-[128px] sm:h-[128px] mb-8 sm:mb-10">
            <div
              className="absolute inset-0 rounded-full"
              style={
                appOpened
                  ? { background: "#3AB1F6" } // static solid ring, no spin
                  : {
                      background:
                        "conic-gradient(from 0deg, #407BFF, #3AB1F6, rgba(58,177,246,0))",
                      animation: "zt-ring-spin 1s linear infinite",
                      WebkitMask:
                        "radial-gradient(farthest-side, transparent calc(100% - 6px), #000 calc(100% - 6px))",
                      mask: "radial-gradient(farthest-side, transparent calc(100% - 6px), #000 calc(100% - 6px))",
                    }
              }
            />
            <div className="absolute inset-[14px] rounded-full bg-white shadow-md flex items-center justify-center">
              <span
                className="text-[#407BFF]"
                style={
                  appOpened
                    ? undefined // stop pulsing once the app is open
                    : { animation: "zt-mic-pulse 1.4s ease-in-out infinite" }
                }
              >
                <MicIcon />
              </span>
            </div>
          </div>

          <h1
            className="font-inter font-semibold text-black text-center text-[22px] sm:text-[28px] md:text-[34px] lg:text-[38px] leading-tight tracking-[-0.03em] mb-3 px-2"
            style={{ animation: "zt-fade 0.5s ease-out both" }}
          >
            {appOpened ? "You’re all set" : "Signing you in…"}
          </h1>
          <p
            className="font-inter font-normal text-[#00000080] text-center text-[14px] sm:text-[16px] md:text-[18px] lg:text-[19.44px] leading-[150%] tracking-[-0.03em] max-w-[584px] mb-6 sm:mb-8 px-2"
            style={{ animation: "zt-fade 0.5s ease-out 0.1s both" }}
          >
            {!deepLink
              ? "We couldn’t find your sign-in details. Please try logging in again from the app."
              : appOpened
                ? "The Zero Typing app is now open. You can close this tab."
                : "You’re signed in. Opening the Zero Typing app — this only takes a moment."}
          </p>

          {deepLink && (
            <p className="font-inter text-[#00000080] text-center text-[13px] sm:text-[14px]">
              {opened ? "Didn’t open automatically? " : "Taking too long? "}
              <a
                href={deepLink}
                onClick={() => setOpened(true)}
                className="text-[#3AB1F6] font-medium hover:underline"
              >
                Open the app
              </a>
            </p>
          )}

          <p className="font-inter text-[#00000080] text-center text-[13px] sm:text-[14px] mt-3">
            Don&apos;t have the app installed?{" "}
            <Link
              to="/download"
              className="text-[#3AB1F6] font-medium hover:underline"
            >
              Download here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignInSuccessful;
